import {useContext} from 'react';

import {useTranslation} from 'react-i18next';

import {Grid, Typography} from '@mui/material';

import {TextOverflowWithTooltip} from '../../../../../../components/TextOverflowWithTooltip/TextOverflowWithTooltip';
import {LanguageContext} from '../../../../../../contexts/LanguageContext/LanguageContext';
import {ApplicantRecruiting, isKeyFactsSalaryRange} from '../../../../../../types/shared-recruiting-types';
import {renderSalaryRange} from '../../../../../../utils/applicantSalaryRange/renderSalaryRange';
import {formatSimpleDuration} from '../../../../../../utils/dateUtils/calculateDuration';
import {
  applicantJobSearchStatus,
  getHighestDegreeDisplay,
  getStartDateDisplay,
  hasLeadershipExperience,
} from '../../../../../../utils/matchKeyFacts/matchKeyFacts';

interface Props {
  applicant?: ApplicantRecruiting;
}

interface KeyFact {
  label: string;
  value: string;
  useTooltip?: boolean;
}

export const ApplicantKeyFacts = ({applicant}: Props) => {
  const {t} = useTranslation();
  const {t: tr} = useTranslation('translation', {keyPrefix: 'companyApplicants.applicantsCard.cardDetails'});
  const currentLanguage = useContext(LanguageContext);

  const leadershipExperience = hasLeadershipExperience(applicant?.keyFacts?.leadershipExperience, t);

  const professionalExperience = formatSimpleDuration(
    {
      amount: applicant?.keyFacts?.professionalExperience?.duration || 0,
      unit: (applicant?.keyFacts?.professionalExperience?.unit || 'years') as 'years' | 'months',
    },
    currentLanguage
  );

  const jobSearchStatus = applicantJobSearchStatus(currentLanguage);

  const startDateDisplay = getStartDateDisplay(applicant?.keyFacts?.startDate, t);

  const highestDegreeDisplay = getHighestDegreeDisplay(applicant?.keyFacts?.highestDegree?.[currentLanguage], t);

  const salaryDisplay = applicant ? renderSalaryRange(applicant, tr) : undefined;

  const keyFacts: KeyFact[] = [];

  if (applicant?.keyFacts?.professionalExperience?.duration) {
    keyFacts.push({label: tr('experience'), value: professionalExperience});
  }
  if (applicant?.keyFacts?.leadershipExperience !== undefined) {
    keyFacts.push({label: tr('leadership'), value: leadershipExperience});
  }
  if (applicant?.keyFacts?.highestDegree?.[currentLanguage]) {
    keyFacts.push({label: tr('highestDegree'), value: highestDegreeDisplay, useTooltip: true});
  }
  if (applicant?.keyFacts?.startDate) {
    keyFacts.push({label: tr('startDate'), value: startDateDisplay});
  }
  const salaryFacts = applicant?.keyFacts?.salary;
  const hasSalary = isKeyFactsSalaryRange(salaryFacts) && (salaryFacts.min || salaryFacts.max);
  if (hasSalary && salaryDisplay) {
    keyFacts.push({label: tr('salary'), value: salaryDisplay, useTooltip: true});
  }
  const statusLabel = jobSearchStatus.find((s) => s.id === applicant?.settings?.jobSearchStatus)?.label;
  if (statusLabel) {
    keyFacts.push({label: tr('status'), value: statusLabel});
  }

  return (
    <Grid container item xs={12} sm={6} direction='column'>
      <Grid item>
        <Typography variant='h3' mb={1}>
          {tr('keyFacts')}
        </Typography>
      </Grid>
      <Grid container item direction='column'>
        {keyFacts.map((fact) => (
          <Grid container item key={fact.label}>
            <Grid item xs={6}>
              <Typography variant='body1'>{fact.label}:</Typography>
            </Grid>
            <Grid item xs={6} sx={{overflow: 'hidden', width: '100%'}}>
              {fact.useTooltip ? (
                <TextOverflowWithTooltip variant='body1' text={fact.value} />
              ) : (
                <Typography variant='body1'>{fact.value}</Typography>
              )}
            </Grid>
          </Grid>
        ))}
      </Grid>
    </Grid>
  );
};
